import { useCallback, useEffect, useRef, useState } from "react";
import { GET_USER } from "../graphql/user";
import httpClient from "../utils/httpClient";
import { parseUrl } from "../utils/url";
import { useQuery } from "@apollo/client";
import { useAuth } from "@frontegg/react";
import { useNavigate } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import { useCheckUserRole, useIsMyOnlyRole } from "../hooks/useCheckUserRole";
import Role from "../enums/Role";
import { getLastSystem } from "../utils/lastSystem";
import { impersonationLanding, resolveLandingRoute } from "../utils/apps";
import { useAppEntitlements } from "../hooks/useAppEntitlements";
import { useImpersonation } from "../contexts/ImpersonationContext";
import WrongAppDialog, { HomeApp } from "../components/auth/WrongAppDialog";
import Layout from "../components/layout/Layout";

const BASE_URL = process.env.REACT_APP_URL;

interface HomeAppResponse {
  recognized: boolean;
  url: string | null;
  name: string | null;
}

export default function Redirect() {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { session: impersonationSession } = useImpersonation();
  const impersonatedRole = impersonationSession?.target?.role ?? null;

  const {
    data: userData,
    loading,
    error,
  } = useQuery(GET_USER, {
    variables: { userId: user?.sub },
    skip: !user?.sub || Boolean(impersonationSession),
  });

  const isAdminOrHigher = useCheckUserRole(Role.Admin);
  const isAuditorOrHigher = useCheckUserRole(Role.Auditor);
  const isManagerOrLower = useCheckUserRole(Role.Manager, { orLower: true });
  const isMyOnly = useIsMyOnlyRole();
  const isPlatformAdminOrHigher = useCheckUserRole(Role.PlatformAdmin);

  const { features: appFeatures } = useAppEntitlements();

  const homeAppRequestedRef = useRef(false);
  const [homeApp, setHomeApp] = useState<HomeApp | null>(null);

  const handleUserQueryError = useCallback(async () => {
    if (homeAppRequestedRef.current) return;
    homeAppRequestedRef.current = true;
    try {
      const { data } = await httpClient.get<HomeAppResponse>(
        "/platform/user/app",
        {
          headers: { Authorization: `Bearer ${user?.accessToken}` },
        },
      );
      if (data.recognized) {
        navigate("/maintenance");
        return;
      }
      const homeUrl = parseUrl(data.url);
      if (!homeUrl || homeUrl.hostname === window.location.hostname) {
        navigate("/logout");
        return;
      }
      setHomeApp({ url: homeUrl.href, name: data.name || homeUrl.hostname });
    } catch {
      navigate("/maintenance");
    }
  }, [user?.accessToken, navigate]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }
    if (impersonationSession) {
      navigate(impersonationLanding(impersonatedRole));
      return;
    }
    if (loading) return;
    if (error) {
      void handleUserQueryError();
      return;
    }
    const status = userData?.user?.company?.status;
    if (user && userData) {
      const storedUrl = localStorage.getItem("_REDIRECT_AFTER_LOGIN_");
      if (storedUrl) {
        window.location.href = `${BASE_URL}${storedUrl}`;
        localStorage.removeItem("_REDIRECT_AFTER_LOGIN_");
        return;
      }

      const route = resolveLandingRoute({
        features: appFeatures,
        status,
        role: userData.user?.role,
        isAdminOrHigher,
        isAuditorOrHigher,
        isManagerOrLower,
        isMyOnly,
        isPlatformAdminOrHigher,
        lastSystem: getLastSystem(),
      });

      if (!route) {
        navigate("/maintenance");
      } else if (route === "/inactive") {
        window.location.href = route;
      } else {
        navigate(route);
      }
    } else {
      navigate("/maintenance");
    }
  }, [
    isAuthenticated,
    user,
    userData,
    loading,
    error,
    navigate,
    isAdminOrHigher,
    isAuditorOrHigher,
    isManagerOrLower,
    isMyOnly,
    isPlatformAdminOrHigher,
    appFeatures,
    impersonationSession,
    impersonatedRole,
    handleUserQueryError,
  ]);

  if (homeApp)
    return (
      <Layout>
        <WrongAppDialog
          open
          homeApp={homeApp}
          onLogout={() => navigate("/logout")}
        />
      </Layout>
    );

  if (loading)
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress color="primary" />
      </Box>
    );

  return null;
}
